import { useCallback, useMemo, useState } from 'react';
import { api, queryString } from '../lib/api.ts';
import { useAsync } from '../lib/useAsync.ts';
import { useAuth } from '../lib/auth.tsx';
import { ActionDetailModal } from '../components/ActionDetailModal.tsx';
import {
  EmptyState,
  ErrorState,
  Field,
  Loading,
  Modal,
  PageHeader,
  PriorityBadge,
  StatusBadge,
} from '../components/ui.tsx';
import { PRIORITY_LABEL, STATUS_LABEL, describeDeadline, formatDate } from '../lib/format.ts';
import type { Action, ActionPriority, ActionStatus, PublicUser } from '../../../shared/types.ts';

const COLUMNS: ActionStatus[] = ['pendente', 'em_andamento', 'concluida'];
const PRIORITIES: ActionPriority[] = ['urgente', 'alta', 'media', 'baixa'];

export function ActionsPage() {
  const { can } = useAuth();
  const [filters, setFilters] = useState({ q: '', priority: '', assigneeId: '', category: '' });
  const [view, setView] = useState<'kanban' | 'lista'>('kanban');
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [justMovedId, setJustMovedId] = useState<string | null>(null);

  const query = queryString(filters);
  const actions = useAsync(() => api.get<Action[]>(`/actions${query}`), [query]);
  const users = useAsync(
    () => (can('superadmin') ? api.get<PublicUser[]>('/users') : Promise.resolve([])),
    [can],
  );

  const categories = useMemo(() => {
    const set = new Set<string>();
    for (const a of actions.data ?? []) if (a.category) set.add(a.category);
    return [...set].sort();
  }, [actions.data]);

  const move = useCallback(
    async (action: Action, status: ActionStatus) => {
      if (action.status === status) return;
      await api.patch(`/actions/${action.id}`, { status });
      setJustMovedId(action.id);
      window.setTimeout(() => setJustMovedId((id) => (id === action.id ? null : id)), 1200);
      actions.reload();
    },
    [actions],
  );

  return (
    <>
      <PageHeader
        title="Ações"
        subtitle={`${actions.data?.length ?? 0} ação(ões) no filtro atual. Arraste entre colunas ou use os botões para mover.`}
        actions={
          can('gerente') && (
            <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
              Nova ação
            </button>
          )
        }
      />

      <div className="toolbar card">
        <input
          className="input"
          type="search"
          placeholder="Buscar por título ou descrição…"
          value={filters.q}
          onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
          aria-label="Buscar ações"
        />
        <select
          className="select"
          value={filters.priority}
          onChange={(e) => setFilters((f) => ({ ...f, priority: e.target.value }))}
          aria-label="Filtrar por prioridade"
        >
          <option value="">Todas as prioridades</option>
          {PRIORITIES.map((p) => (
            <option key={p} value={p}>
              {PRIORITY_LABEL[p]}
            </option>
          ))}
        </select>
        <select
          className="select"
          value={filters.category}
          onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))}
          aria-label="Filtrar por horizonte"
        >
          <option value="">Todos os horizontes</option>
          {categories.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <div className="tabs" role="tablist" aria-label="Modo de visualização">
          {(['kanban', 'lista'] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              role="tab"
              aria-selected={view === mode}
              className={`tab${view === mode ? ' is-active' : ''}`}
              onClick={() => setView(mode)}
            >
              {mode === 'kanban' ? 'Quadro' : 'Lista'}
            </button>
          ))}
        </div>
      </div>

      {actions.loading && <Loading label="Carregando ações…" />}
      {actions.error && <ErrorState message={actions.error} onRetry={actions.reload} />}

      {actions.data && actions.data.length === 0 && (
        <EmptyState
          title="Nenhuma ação com esses filtros."
          hint="Limpe a busca ou crie uma ação nova."
        />
      )}

      {actions.data && actions.data.length > 0 && view === 'kanban' && (
        <div className="kanban">
          {COLUMNS.map((status) => {
            const items = actions.data!.filter((a) => a.status === status);
            return (
              <section
                key={status}
                className="kanban__column"
                onDragOver={(e) => {
                  if (can('gerente')) e.preventDefault();
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const id = e.dataTransfer.getData('text/plain');
                  const action = actions.data?.find((a) => a.id === id);
                  if (action) void move(action, status);
                }}
                aria-label={STATUS_LABEL[status]}
              >
                <header className="kanban__header">
                  <h2 className="text-label">{STATUS_LABEL[status]}</h2>
                  <span className="badge badge-status-neutral tabular-nums">{items.length}</span>
                </header>

                <div className="kanban__cards">
                  {items.map((action) => (
                    <ActionCard
                      key={action.id}
                      action={action}
                      canEdit={can('gerente')}
                      justMoved={action.id === justMovedId}
                      onOpen={() => setOpenId(action.id)}
                      onMove={(next) => void move(action, next)}
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {actions.data && actions.data.length > 0 && view === 'lista' && (
        <div className="card table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Ação</th>
                <th>Prioridade</th>
                <th>Situação</th>
                <th>Horizonte</th>
                <th>Prazo</th>
              </tr>
            </thead>
            <tbody>
              {actions.data.map((action) => (
                <tr key={action.id} onClick={() => setOpenId(action.id)} className="row--clickable">
                  <td>{action.title}</td>
                  <td>
                    <PriorityBadge priority={action.priority} />
                  </td>
                  <td>
                    <StatusBadge status={action.status} />
                  </td>
                  <td className="text-muted">{action.category ?? '—'}</td>
                  <td className="text-muted tabular-nums">{formatDate(action.dueDate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <ActionForm
          users={users.data ?? []}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            actions.reload();
          }}
        />
      )}

      {openId && (
        <ActionDetailModal
          id={openId}
          canEdit={can('gerente')}
          onClose={() => setOpenId(null)}
          onChanged={actions.reload}
        />
      )}
    </>
  );
}

function ActionCard({
  action,
  canEdit,
  justMoved,
  onOpen,
  onMove,
}: {
  action: Action;
  canEdit: boolean;
  justMoved?: boolean;
  onOpen: () => void;
  onMove: (status: ActionStatus) => void;
}) {
  const deadline = describeDeadline(action.dueDate);
  const late = deadline?.startsWith('Atrasada') ?? false;

  return (
    <article
      className={`card card--interactive action-card${justMoved ? ' action-card--landed' : ''}`}
      draggable={canEdit}
      onDragStart={(e) => e.dataTransfer.setData('text/plain', action.id)}
    >
      <button type="button" className="action-card__title" onClick={onOpen}>
        {action.title}
      </button>

      <div className="action-card__meta">
        <PriorityBadge priority={action.priority} />
        {action.category && <span className="text-micro text-muted">{action.category}</span>}
      </div>

      {deadline && (
        <span className={`text-micro ${late ? 'text-danger' : 'text-muted'}`}>{deadline}</span>
      )}

      {canEdit && (
        // Botões além do arrastar: teclado e leitor de tela não têm drag-and-drop.
        <div className="action-card__moves">
          {COLUMNS.filter((s) => s !== action.status).map((status) => (
            <button
              key={status}
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => onMove(status)}
            >
              → {STATUS_LABEL[status]}
            </button>
          ))}
        </div>
      )}
    </article>
  );
}

function ActionForm({
  users,
  onClose,
  onSaved,
}: {
  users: PublicUser[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    title: '',
    description: '',
    priority: 'media' as ActionPriority,
    dueDate: '',
    assigneeId: '',
    category: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!form.title.trim()) {
      setError('Informe o título.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.post('/actions', {
        title: form.title.trim(),
        description: form.description.trim() || null,
        priority: form.priority,
        dueDate: form.dueDate || null,
        assigneeId: form.assigneeId || null,
        category: form.category.trim() || null,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível salvar.');
      setSaving(false);
    }
  }

  return (
    <Modal
      title="Nova ação"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void save()}
            disabled={saving}
          >
            {saving ? 'Salvando…' : 'Criar ação'}
          </button>
        </>
      }
    >
      <div className="stack">
        <Field label="Título">
          <input
            className="input"
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          />
        </Field>

        <Field label="Descrição">
          <textarea
            className="textarea"
            rows={4}
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          />
        </Field>

        <div className="row">
          <Field label="Prioridade">
            <select
              className="select"
              value={form.priority}
              onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value as ActionPriority }))}
            >
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {PRIORITY_LABEL[p]}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Prazo">
            <input
              className="input"
              type="date"
              value={form.dueDate}
              onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))}
            />
          </Field>
        </div>

        <div className="row">
          <Field label="Horizonte" hint="Ex.: imediato, 7d, 30d, 90d">
            <input
              className="input"
              value={form.category}
              onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
            />
          </Field>

          {users.length > 0 && (
            <Field label="Responsável">
              <select
                className="select"
                value={form.assigneeId}
                onChange={(e) => setForm((f) => ({ ...f, assigneeId: e.target.value }))}
              >
                <option value="">Sem responsável</option>
                {users
                  .filter((u) => u.active)
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
              </select>
            </Field>
          )}
        </div>

        {error && (
          <p className="state-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
